import useOnMount from '@rio-cloud/rio-uikit/hooks/useOnMount';
import maxBy from 'lodash/fp/maxBy';
import minBy from 'lodash/fp/minBy';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { type NavigateFunction, useLocation, useNavigate } from 'react-router';
import {
    arrayRemoveAll,
    autofill,
    Field,
    formValueSelector as formValueSelectorCreator,
    type InjectedFormProps,
    reduxForm,
} from 'redux-form';

import {
    dataLayerPush,
    GAEventName,
    GAEventTrigger,
    GAFlowName,
} from '../../../configuration/googleAnalytics/googleAnalytics';
import type { Dispatch, State } from '../../../types';
import { getGeofence as triggerFetchGeofence } from '../../actions/geofenceActions';
import { setMapBoundingBox, setMapZoom, undoPolygonPoint } from '../../actions/mapActions';
import { Routing } from '../../app/Routing';
import { onboardingActions } from '../../onboarding/redux/oboarding.redux';
import { Tips } from '../../onboarding/types';
import { getGeofence as geofenceSelector } from '../../reducers/geofenceReducer';
import { DEFAULT_ZOOM } from '../../reducers/mapReducer';
import { getEditMode } from '../../reducers/selectors';
import { transformLocalToGlobalCoordinates } from '../../utils/coordinateTransformer';
import { required } from '../../validation/required';
import Checkbox from '../common/formInputs/Checkbox';
import ClearableInput from '../common/formInputs/ClearableInput';
import { CategoryInput } from './CategoryInput';
import { CircularFormOptions } from './CircularFormOptions';
import { PolygonalFormOptions } from './PolygonalFormOptions';
import ShapeSelector from './ShapeSelector';

type GeofenceFormProps = {
    onDeletePolygon: () => void;
    onUndoPolygonPoint: () => void;
    isPolygonal: boolean;
    isCircular: boolean;
    numberOfPointsDrawn: number;
    validShape?: boolean;
    geofenceId?: string;
    fetchGeofence: (geofenceId: string, navigate: NavigateFunction) => Promise<void>;
    setZoom: (zoom: number) => void;
    geofencePosition: { lat: number; lng: number };
    geofenceRadius: number;
    onCenterMap: (geofencePosition: { lat: number; lng: number }, geofenceRadius: number) => () => void;
};

export const geofenceFormElements = {
    geofenceName: 'geofenceName',
    geofenceFormName: 'geofenceCreation',
    activateGeofence: 'activateGeofence',
    geofenceShape: 'geofenceShape',
    geofencePoints: 'points',
    geofenceSuggestionPoint: 'suggestionPoint',
    geofenceAddress: 'geofenceAddress',
    geofenceRadius: 'geofenceRadius',
    geofenceCategory: 'geofenceCategory',
    geofenceId: 'geofenceId',
};

export const geofenceCreationFormInitialValues = {
    [geofenceFormElements.geofencePoints]: [],
    [geofenceFormElements.geofenceRadius]: 500,
    [geofenceFormElements.geofenceShape]: 'circular',
    [geofenceFormElements.activateGeofence]: true,
};

const ShapeSelectorWrapper = ({ editMode }: { editMode: boolean }) => {
    if (editMode) {
        return null;
    }
    return (
        <div className='margin-bottom-15'>
            <label>
                <FormattedMessage id='intl-msg:fleetmonitor.geofence.shape' />
            </label>
            <ShapeSelector />
        </div>
    );
};

export const GeofenceForm = (props: GeofenceFormProps & Partial<InjectedFormProps>) => {
    const {
        isPolygonal,
        isCircular,
        numberOfPointsDrawn,
        validShape,
        geofenceId,
        onDeletePolygon,
        onUndoPolygonPoint,
        fetchGeofence,
        handleSubmit,
        setZoom,
        geofencePosition,
        geofenceRadius,
        onCenterMap,
    } = props;

    const location = useLocation();
    const navigate = useNavigate();
    const editMode = !location.pathname.includes('create');

    const [geofenceFetched, setGeofenceFetched] = useState(false);

    useOnMount(() => {
        if (editMode && geofenceId) {
            fetchGeofence(geofenceId, navigate).then(() => {
                setGeofenceFetched(true);
            });
        } else {
            setZoom(DEFAULT_ZOOM);
        }
    }, []);

    return (
        (!editMode || geofenceFetched) && (
            <form onSubmit={handleSubmit}>
                <div className='margin-bottom-15'>
                    <label>
                        <FormattedMessage id='intl-msg:fleetmonitor.name' />
                    </label>
                    <Field
                        name={geofenceFormElements.geofenceName}
                        component={ClearableInput}
                        type='text'
                        placeholder='Name'
                        validate={required}
                    />
                </div>
                <CategoryInput name={geofenceFormElements.geofenceCategory} />
                <div className='margin-bottom-15'>
                    <Field
                        name={geofenceFormElements.activateGeofence}
                        component={Checkbox}
                        checkboxLabel={<FormattedMessage id='intl-msg:fleetmonitor.activateGeofence' />}
                    />
                </div>
                <hr className='border-width-1 text-color-light' />
                <div className='text-bold text-size-large margin-top-15 margin-bottom-15'>
                    <FormattedMessage id='intl-msg:geofence.settings' />
                </div>
                <ShapeSelectorWrapper editMode={editMode} />
                <PolygonalFormOptions
                    isPolygonal={isPolygonal}
                    editMode={editMode}
                    validShape={validShape}
                    numberOfPointsDrawn={numberOfPointsDrawn}
                    onDeletePolygon={onDeletePolygon}
                    onUndoPolygonPoint={onUndoPolygonPoint}
                />
                <CircularFormOptions
                    isCircular={isCircular}
                    editMode={editMode}
                    onCenterMap={onCenterMap(geofencePosition, geofenceRadius)}
                />
            </form>
        )
    );
};

const mapStateToProps = (state: State) => {
    const formSelector = formValueSelectorCreator(geofenceFormElements.geofenceFormName);
    const shape = formSelector(state, geofenceFormElements.geofenceShape);
    const listOfAllPoints = formSelector(state, geofenceFormElements.geofencePoints);
    const radius = formSelector(state, geofenceFormElements.geofenceRadius);

    return {
        isPolygonal: shape === 'polygonal',
        isCircular: shape === 'circular',
        editMode: getEditMode(state) === 'UPDATE',
        numberOfPointsDrawn: listOfAllPoints?.length ?? 0,
        geofencePosition: listOfAllPoints?.[0],
        geofenceRadius: radius,
    };
};

const doFetchGeofence = (geofenceId: string) => (dispatch: Dispatch, getStore: () => State) =>
    dispatch(triggerFetchGeofence(geofenceId)).then(() => {
        const geofence = geofenceSelector(getStore().geofence, geofenceId);

        const transformShape = (shape: string) =>
            shape === 'CIRCLE' ? 'circular' : shape === 'POLYGON' ? 'polygonal' : shape;

        if (geofence) {
            const formName = geofenceFormElements.geofenceFormName;

            dispatch(autofill(formName, geofenceFormElements.geofenceShape, transformShape(geofence.type)));
            dispatch(autofill(formName, geofenceFormElements.geofenceId, geofenceId));
            dispatch(autofill(formName, geofenceFormElements.geofenceName, geofence.name));
            dispatch(autofill(formName, geofenceFormElements.activateGeofence, geofence.active));
            dispatch(autofill(formName, geofenceFormElements.geofenceCategory, geofence.category));

            if (geofence.type === 'CIRCLE') {
                dispatch(autofill(formName, geofenceFormElements.geofenceRadius, geofence.radius));
                dispatch(autofill(formName, geofenceFormElements.geofenceAddress, geofence.center.address));
                dispatch(
                    autofill(formName, geofenceFormElements.geofencePoints, [
                        { lat: geofence.center.latitude, lng: geofence.center.longitude },
                    ])
                );
                centerMapOnGeofence(
                    {
                        lng: geofence.center.longitude,
                        lat: geofence.center.latitude,
                    },
                    geofence.radius,
                    dispatch
                );
            } else if (geofence.type === 'POLYGON') {
                if (geofence?.points && geofence.points.length > 0) {
                    const top = maxBy(it => it.latitude, geofence.points)?.latitude!;
                    const bottom = minBy(it => it.latitude, geofence.points)?.latitude!;
                    const right = maxBy(it => it.longitude, geofence.points)?.longitude!;
                    const left = minBy(it => it.longitude, geofence.points)?.longitude!;
                    dispatch(setMapBoundingBox({ top, bottom, left, right }));
                }
                dispatch(
                    autofill(
                        formName,
                        geofenceFormElements.geofencePoints,
                        geofence.points.map(point => ({ lat: point.latitude, lng: point.longitude }))
                    )
                );
            }
        }
    });

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        fetchGeofence: (geofenceId: string, navigate: NavigateFunction) =>
            dispatch(doFetchGeofence(geofenceId)).catch(() => navigate(Routing.poiList)),
        onDeletePolygon: () => {
            dataLayerPush({
                trigger: GAEventTrigger.click,
                event: GAEventName.polygonalGeofenceMarkersReset,
                flow_name: GAFlowName.createGeofence,
            });
            dispatch(arrayRemoveAll(geofenceFormElements.geofenceFormName, geofenceFormElements.geofencePoints));
        },
        onUndoPolygonPoint: () => {
            dataLayerPush({
                trigger: GAEventTrigger.click,
                event: GAEventName.polygonalGeofenceLastMarkerRemoved,
                flow_name: GAFlowName.createGeofence,
            });
            // dispatch(arrayPop(geofenceFormElements.geofenceFormName, geofenceFormElements.geofencePoints));
            dispatch(undoPolygonPoint());
            dispatch(
                onboardingActions.setShowOnboardingTip({
                    type: Tips.POLYGONAL_GEOFENCE_CREATION_UNDO_BUTTON,
                    value: false,
                })
            );
        },
        setZoom: (zoom: number) => dispatch(setMapZoom(zoom)),
        onCenterMap: (geofencePosition: { lat: number; lng: number }, geofenceRadius: number) => () => {
            centerMapOnGeofence(geofencePosition, geofenceRadius, dispatch);
        },
    };
};

const centerMapOnGeofence = (
    geofencePosition: {
        lat: number;
        lng: number;
    },
    geofenceRadius: number,
    dispatch: Dispatch
) => {
    if (geofencePosition && geofenceRadius) {
        const boundingBox = calculateBoundingBoxForCircle(geofencePosition, 2 * geofenceRadius);
        dispatch(setMapBoundingBox(boundingBox));
    }
};

const calculateBoundingBoxForCircle = (center: { lat: number; lng: number }, radiusInMeters: number) => {
    const top = transformLocalToGlobalCoordinates({ x: 0, y: radiusInMeters }, center).lat;
    const bottom = transformLocalToGlobalCoordinates({ x: 0, y: -radiusInMeters }, center).lat;
    const right = transformLocalToGlobalCoordinates({ x: radiusInMeters, y: 0 }, center).lng;
    const left = transformLocalToGlobalCoordinates({ x: -radiusInMeters, y: 0 }, center).lng;
    return { top, bottom, left, right };
};

const ConnectedGeofenceForm = connect(mapStateToProps, mapDispatchToProps)(GeofenceForm);

export default reduxForm<{}, { geofenceId?: string; validShape?: boolean }>({
    form: geofenceFormElements.geofenceFormName,
    initialValues: geofenceCreationFormInitialValues,
})(ConnectedGeofenceForm as any);
